<template>
  <span v-if="blank" aria-hidden="true" />

  <div
    v-else-if="tag === 'CURRENT'"
    class="bid-pad-cell bid-pad-cell--current"
    title="Current accepted bid — reference only, bids cannot be lowered"
  >
    ${{ price.toLocaleString() }}
    <span class="bid-pad-cell__tag">CURRENT</span>
  </div>

  <div
    v-else-if="tag === 'ASKING'"
    class="bid-pad-cell bid-pad-cell--asking"
    title="Auctioneer's current asking — tap a lower cell to drop and fish for an opening bid"
  >
    ${{ price.toLocaleString() }}
    <span class="bid-pad-cell__tag">ASKING</span>
  </div>

  <button
    v-else
    class="bid-pad-cell bid-pad-cell--tappable"
    :class="`bid-pad-cell--tappable-${tone}`"
    :disabled="disabled"
    @click="emit('bid', price)"
  >
    ${{ price.toLocaleString() }}
  </button>
</template>

<script setup>
import { computed } from 'vue';

// One price on the floor bid pad.
//
// Three shapes, and only one of them is a button: the CURRENT and ASKING cells
// are reference marks the clerk reads against, and making them tappable would
// let a mis-aimed thumb re-bid the price that is already standing.
//
// The pad is built as a fixed grid, so a cell with no price is a real cell —
// it renders an empty placeholder to hold its track rather than collapsing the
// grid around it.
defineOptions({ name: 'BidPadCell' });

const props = defineProps({
  // The cell's price. Null or non-positive renders the placeholder.
  price: { type: Number, default: null },
  // CURRENT | ASKING | null — which reference mark this cell is, if any.
  tag: { type: String, default: null, validator: (value) => value === null || ['CURRENT', 'ASKING'].includes(value) },
  // primary once bids are coming in, info while the auctioneer is still asking
  // — the same two-direction signal the increment ladder uses.
  tone: { type: String, default: 'primary', validator: (value) => ['primary', 'info'].includes(value) },
  // The sale is finalized; the pad is locked.
  disabled: { type: Boolean, default: false },
});

const emit = defineEmits(['bid']);

const blank = computed(() => props.price == null || props.price <= 0);
</script>

<style scoped>
.bid-pad-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  font-variant-numeric: tabular-nums;
  border-radius: var(--p-bid-pad-cell-border-radius);
  padding: var(--p-bid-pad-cell-padding);
  font-family: var(--p-bid-pad-cell-font-family);
  font-size: var(--p-bid-pad-cell-font-size);
  letter-spacing: var(--p-bid-pad-cell-letter-spacing);
}

/* ── Reference marks ────────────────────────────────────────────────────── */

.bid-pad-cell--current,
.bid-pad-cell--asking {
  cursor: default;
  font-weight: var(--p-bid-pad-cell-reference-font-weight);
  border-width: var(--p-bid-pad-cell-reference-border-width);
  border-style: solid;
}

.bid-pad-cell--current {
  background: var(--p-bid-pad-cell-current-background);
  color: var(--p-bid-pad-cell-current-color);
  border-color: var(--p-bid-pad-cell-current-border-color);
  box-shadow: var(--p-bid-pad-cell-current-box-shadow);
}

.bid-pad-cell--asking {
  background: var(--p-bid-pad-cell-asking-background);
  color: var(--p-bid-pad-cell-asking-color);
  border-color: var(--p-bid-pad-cell-asking-border-color);
  box-shadow: var(--p-bid-pad-cell-asking-box-shadow);
}

.bid-pad-cell__tag {
  position: absolute;
  top: 4px;
  left: 6px;
  opacity: 0.85;
  color: inherit;
  font-family: var(--p-bid-pad-cell-tag-font-family);
  font-size: var(--p-bid-pad-cell-tag-font-size);
  font-weight: var(--p-bid-pad-cell-tag-font-weight);
  letter-spacing: var(--p-bid-pad-cell-tag-letter-spacing);
}

/* ── The tappable cell ──────────────────────────────────────────────────── */

.bid-pad-cell--tappable {
  cursor: pointer;
  transition: all 100ms cubic-bezier(0.2, 0, 0, 1);
  background: var(--p-bid-pad-cell-tappable-background);
  color: var(--p-bid-pad-cell-tappable-color);
  border: var(--p-bid-pad-cell-tappable-border-width) solid var(--p-bid-pad-cell-tappable-border-color);
  font-weight: var(--p-bid-pad-cell-tappable-font-weight);
  box-shadow: var(--p-bid-pad-cell-tappable-box-shadow);
}

.bid-pad-cell--tappable-primary:hover:not(:disabled) {
  background: var(--p-bid-pad-cell-tappable-primary-hover-background);
  border-color: var(--p-bid-pad-cell-tappable-primary-hover-border-color);
}

.bid-pad-cell--tappable-info:hover:not(:disabled) {
  background: var(--p-bid-pad-cell-tappable-info-hover-background);
  border-color: var(--p-bid-pad-cell-tappable-info-hover-border-color);
}

.bid-pad-cell--tappable:disabled {
  cursor: not-allowed;
  opacity: 0.4;
}
</style>
